// js/site.js — vanilla JS for wildandthebarre.com.
// Bundled to dist/site.min.js by build.mjs (esbuild).
//
// Replaces the old jQuery + RD Navbar + WOW + script.js + core.min.js stack.
// Provides:
//   - copyright year stamp (#copyright-year)
//   - RD Navbar replacement (mobile burger toggle, desktop dropdown-on-hover,
//     viewport-based layout switching, sticky-on-scroll)
//   - parallax-header iOS fallback (background-attachment: fixed bails on iOS)
//   - mailto analytics (gtag click event)
//   - IntersectionObserver scroll-reveal utility for [data-reveal] (Phase 3 hook)

(() => {
  'use strict';

  // ---------------------------------------------------------------------------
  // Copyright year
  // ---------------------------------------------------------------------------
  const yearEl = document.querySelector('#copyright-year');
  if (yearEl) {
    yearEl.textContent = String(new Date().getFullYear());
  }

  // ---------------------------------------------------------------------------
  // RD Navbar replacement
  // ---------------------------------------------------------------------------
  // The original jsdesign RD Navbar reads layout per breakpoint from data-attrs:
  //   data-layout (default), data-sm-layout, data-md-layout, data-lg-layout, data-xl-layout
  // Values relevant to this site: "rd-navbar-fixed" or "rd-navbar-static".
  // Breakpoints match the original RD Navbar 4.x responsive map:
  //   default <768, sm 768..991, md 992..1399, lg/xl ≥1400
  // The site's nav is fixed at <992 and static at ≥992 (see index.html attrs).

  const navbars = document.querySelectorAll('.rd-navbar');

  // RD Navbar 4.x responsive breakpoints: 0 / 768 / 992 / 1400
  // Tier names: default(<768) / sm(768) / md(992) / lg(1400)
  function getNavbarLayout(nav) {
    const w = window.innerWidth;
    let attr;
    if (w >= 1400 && nav.dataset.lgLayout) attr = nav.dataset.lgLayout;
    else if (w >= 992 && nav.dataset.mdLayout) attr = nav.dataset.mdLayout;
    else if (w >= 768 && nav.dataset.smLayout) attr = nav.dataset.smLayout;
    else attr = nav.dataset.layout || 'rd-navbar-fixed';
    return attr;
  }

  function getStickUpOffset(nav) {
    const w = window.innerWidth;
    let raw;
    if (w >= 1400 && nav.dataset.lgStickUpOffset) raw = nav.dataset.lgStickUpOffset;
    else if (w >= 992 && nav.dataset.mdStickUpOffset) raw = nav.dataset.mdStickUpOffset;
    else if (w >= 768 && nav.dataset.smStickUpOffset) raw = nav.dataset.smStickUpOffset;
    else raw = nav.dataset.stickUpOffset;
    if (!raw) return 0;
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : 0;
  }

  function isStickUpEnabled(nav) {
    const w = window.innerWidth;
    if (w >= 1400) return nav.dataset.lgStickUp === 'true';
    if (w >= 992) return nav.dataset.mdStickUp === 'true';
    if (w >= 768) return nav.dataset.smStickUp === 'true';
    return nav.dataset.stickUp === 'true';
  }

  function applyLayout(nav) {
    const layout = getNavbarLayout(nav);
    // Strip both layout classes then apply the active one.
    nav.classList.remove('rd-navbar-fixed', 'rd-navbar-static', 'rd-navbar-fullwidth', 'rd-navbar-sidebar');
    nav.classList.add(layout);

    // body class hook used by some CSS (e.g. rd-navbar-static-smooth).
    if (nav.dataset.bodyClass) {
      const cls = nav.dataset.bodyClass;
      // Only apply on layouts that match (the original adds this for static).
      if (layout === 'rd-navbar-static') {
        document.body.classList.add(cls);
      } else {
        document.body.classList.remove(cls);
      }
    }
  }

  // When the navbar is `position: fixed` (mobile fixed layout, or desktop
  // static-smooth at ≥992px), it's taken out of document flow and the parent
  // `.rd-navbar-wrap` collapses to height 0 — content below then renders under
  // the navbar. The original RD Navbar JS solved this by injecting a clone
  // element; we mirror the effect by setting min-height on the wrap to match
  // the navbar's rendered height.
  function reserveNavbarSpace(nav) {
    const wrap = nav.closest('.rd-navbar-wrap');
    if (!wrap) return;
    const pos = getComputedStyle(nav).position;
    if (pos === 'fixed') {
      wrap.style.minHeight = nav.offsetHeight + 'px';
    } else {
      wrap.style.minHeight = '';
    }
  }

  function bindNavbar(nav) {
    applyLayout(nav);

    // Burger toggle: <button data-rd-navbar-toggle="<selector>">
    nav.querySelectorAll('[data-rd-navbar-toggle]').forEach((btn) => {
      const sel = btn.getAttribute('data-rd-navbar-toggle');
      if (!sel) return;
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const open = btn.classList.toggle('active');
        btn.setAttribute('aria-expanded', String(open));
        nav.querySelectorAll(sel).forEach((target) => {
          target.classList.toggle('active');
        });
      });
    });

    // Dropdown-on-hover (desktop) and click (mobile) for li with .rd-navbar-dropdown.
    nav.querySelectorAll('.rd-navbar-nav > li').forEach((li) => {
      const dd = li.querySelector(':scope > .rd-navbar-dropdown, :scope > .rd-navbar-megamenu');
      if (!dd) return;
      li.classList.add('rd-navbar-submenu');
      // The static/fullwidth dropdown CSS keys off `.rd-navbar--has-dropdown`
      // to set `position: relative` on the parent li. Without it, the dropdown
      // positions absolute relative to the document (far left), not the menu item.
      li.classList.add('rd-navbar--has-dropdown');

      // Inject the submenu-toggle span (caret) that RD Navbar's JS originally
      // appended at runtime. CSS targets it via .rd-navbar-submenu-toggle::after.
      if (!li.querySelector(':scope > .rd-navbar-submenu-toggle')) {
        const caret = document.createElement('span');
        caret.className = 'rd-navbar-submenu-toggle';
        // Insert immediately after the anchor so adjacent-sibling selectors
        // (`a:hover + .rd-navbar-submenu-toggle`) still match.
        const anchor = li.querySelector(':scope > a');
        if (anchor && anchor.nextSibling) {
          li.insertBefore(caret, anchor.nextSibling);
        } else if (anchor) {
          li.appendChild(caret);
        } else {
          li.insertBefore(caret, li.firstChild);
        }
      }

      li.addEventListener('mouseenter', () => {
        if (window.innerWidth >= 992) li.classList.add('opened');
      });
      li.addEventListener('mouseleave', () => {
        if (window.innerWidth >= 992) li.classList.remove('opened');
      });

      // Mobile/touch: tap the caret toggles the submenu in fixed (mobile) layout.
      const caret = li.querySelector(':scope > .rd-navbar-submenu-toggle');
      if (caret) {
        caret.addEventListener('click', (e) => {
          if (nav.classList.contains('rd-navbar-fixed')) {
            e.preventDefault();
            e.stopPropagation();
            li.classList.toggle('opened');
          }
        });
      }
    });

    // Sticky-on-scroll
    const updateStuck = () => {
      if (!isStickUpEnabled(nav)) {
        nav.classList.remove('rd-navbar--is-stuck');
        return;
      }
      const offset = getStickUpOffset(nav);
      if (window.scrollY > offset) {
        nav.classList.add('rd-navbar--is-stuck');
      } else {
        nav.classList.remove('rd-navbar--is-stuck');
      }
    };
    window.addEventListener('scroll', updateStuck, { passive: true });
    updateStuck();

    // Reserve wrap space on initial load, after fonts/images settle, and on
    // resize — the navbar's rendered height depends on font loading and
    // wrapping behavior.
    reserveNavbarSpace(nav);
    if (document.readyState !== 'complete') {
      window.addEventListener('load', () => reserveNavbarSpace(nav), { once: true });
    }
  }

  navbars.forEach(bindNavbar);

  // Re-evaluate layout on resize (debounced).
  let resizeTimer = null;
  window.addEventListener('resize', () => {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      navbars.forEach((nav) => {
        applyLayout(nav);
        reserveNavbarSpace(nav);
      });
    }, 150);
  });

  // ---------------------------------------------------------------------------
  // User-agent / device classes on <html>. The original core.min.js adds
  // `.desktop` / `.mobile` / `.tablet` and `.touch` / `.no-touch` / `.firefox` /
  // `.mac-os` etc. We only need the ones existing CSS keys off:
  //   - .mobile / .tablet (used to hide .ui-to-top on phones/tablets)
  //   - .firefox          (used in a couple of overflow tweaks)
  //   - .mac-os           (used in a couple of typography tweaks)
  // ---------------------------------------------------------------------------
  const ua = navigator.userAgent;
  const html = document.documentElement;
  const isPhone = /iPhone|iPod|Android.*Mobile|Mobile.*Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const isTablet = /iPad|Android(?!.*Mobile)|Tablet/i.test(ua);
  if (isPhone) html.classList.add('mobile');
  else if (isTablet) html.classList.add('tablet');
  else html.classList.add('desktop');

  if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
    html.classList.add('touch');
  } else {
    html.classList.add('no-touch');
  }
  if (/Firefox/i.test(ua)) html.classList.add('firefox');
  if (/Macintosh|Mac OS/i.test(ua)) html.classList.add('mac-os');

  // ---------------------------------------------------------------------------
  // Back-to-top button. CSS hides it via `.mobile .ui-to-top, .tablet .ui-to-top`
  // (set above), so we can safely render it everywhere — phones won't see it.
  // ---------------------------------------------------------------------------
  const toTop = document.createElement('a');
  toTop.className = 'ui-to-top';
  toTop.href = '#';
  toTop.setAttribute('aria-label', 'Back to top');
  document.body.appendChild(toTop);

  toTop.addEventListener('click', (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });

  const updateToTop = () => {
    // Original threshold: visible after scrolling >400px (UItoTop default).
    if (window.scrollY > 400) {
      toTop.classList.add('active');
    } else {
      toTop.classList.remove('active');
    }
  };
  window.addEventListener('scroll', updateToTop, { passive: true });
  updateToTop();

  // ---------------------------------------------------------------------------
  // Parallax-header iOS fallback
  // CSS sets `background-attachment: fixed` on .parallax-header. iOS Safari
  // ignores fixed bg and scrolls it. We mark the html element so CSS can opt
  // out of the fixed declaration on iOS.
  // ---------------------------------------------------------------------------
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (isIOS) {
    document.documentElement.classList.add('is-ios');
  }

  // ---------------------------------------------------------------------------
  // Mailto analytics
  // ---------------------------------------------------------------------------
  document.querySelectorAll('a[href^="mailto:"]').forEach((a) => {
    a.addEventListener('click', () => {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'click', {
          event_category: 'button',
          event_label: 'Email!',
        });
      }
    });
  });

  // ---------------------------------------------------------------------------
  // Reviews carousel
  // ---------------------------------------------------------------------------
  // Renders <section data-reviews-carousel data-src="data/reviews.json"> from a
  // JSON file at runtime. Vanilla JS, ~1.5kB minified. Uses CSS scroll-snap for
  // touch/swipe and IntersectionObserver to track the active slide for the dots
  // indicator. Honors prefers-reduced-motion (no autoplay, instant scroll).
  //
  // JSON shape:
  //   { "reviews": [ { id, name, rating, text, source, date, avatar }, ... ] }
  // avatar may be null — renderer falls back to a CSS-styled initials circle.
  function initReviewsCarousel() {
    const root = document.querySelector('[data-reviews-carousel]');
    if (!root) return;
    const src = root.getAttribute('data-src');
    if (!src) return;

    const track = root.querySelector('[data-reviews-track]');
    const dotsList = root.querySelector('[data-reviews-dots]');
    const liveRegion = root.querySelector('[data-reviews-live]');
    const prevBtn = root.querySelector('.reviews-carousel__btn--prev');
    const nextBtn = root.querySelector('.reviews-carousel__btn--next');
    const viewport = root.querySelector('.reviews-carousel__viewport');
    const playPauseBtn = root.querySelector('[data-reviews-play-pause]');
    if (!track || !dotsList || !prevBtn || !nextBtn || !viewport) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const AUTOPLAY_MS = 3000;

    function escapeHtml(s) {
      return String(s).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      }[c]));
    }

    function initials(name) {
      const parts = String(name || '').replace(/\./g, '').trim().split(/\s+/);
      const first = parts[0] || '';
      const second = parts[parts.length - 1] || '';
      return ((first[0] || '') + (parts.length > 1 ? second[0] || '' : '')).toUpperCase();
    }

    function shuffle(arr) {
      // Fisher-Yates — present reviews in random order on each load so visitors
      // see different ones over time.
      const a = arr.slice();
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    }

    // Appends one slide per review after the slides already in the track
    // (the static first slide in the HTML). renumberSlides() sets the final
    // aria-label / data-index values afterwards.
    function renderReviews(reviews) {
      const offset = track.children.length;
      reviews.forEach((r, i) => {
        const idx = offset + i;
        const slide = document.createElement('div');
        slide.className = 'reviews-carousel__slide';
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute('aria-label', `${idx + 1} of ${offset + reviews.length}`);
        slide.dataset.index = String(idx);

        const avatarHtml = r.avatar
          ? `<img class="reviews-carousel__avatar-img" src="${escapeHtml(r.avatar)}" alt="" loading="lazy" width="74" height="74">`
          : `<span class="reviews-carousel__avatar-initials" aria-hidden="true">${escapeHtml(initials(r.name))}</span>`;

        // Star marks (filled / empty)
        const ratingFloor = Math.max(0, Math.min(5, Math.floor(r.rating || 0)));
        let stars = '';
        for (let s = 0; s < 5; s++) {
          stars += s < ratingFloor
            ? '<span class="reviews-carousel__star reviews-carousel__star--filled" aria-hidden="true">★</span>'
            : '<span class="reviews-carousel__star" aria-hidden="true">☆</span>';
        }

        const sourceLabel = r.source ? `<span class="reviews-carousel__source"> · ${escapeHtml(r.source)}</span>` : '';
        const dateLabel = r.date ? `<span class="reviews-carousel__date"> · ${escapeHtml(r.date)}</span>` : '';

        slide.innerHTML = `
          <div class="quote-default quote-default_left-v2 reviews-carousel__quote">
            <div class="quote-default__image reviews-carousel__avatar">${avatarHtml}</div>
            <div class="reviews-carousel__rating" role="img" aria-label="${ratingFloor} out of 5 stars">${stars}</div>
            <div class="quote-default__text reviews-carousel__text">
              <p class="q">${escapeHtml(r.text)}</p>
            </div>
            <p class="quote-default__cite reviews-carousel__cite">${escapeHtml(r.name)}${sourceLabel}${dateLabel}</p>
          </div>`;
        track.appendChild(slide);
      });
    }

    function renumberSlides() {
      const slides = Array.from(track.children);
      slides.forEach((slide, idx) => {
        slide.setAttribute('aria-label', `${idx + 1} of ${slides.length}`);
        slide.dataset.index = String(idx);
      });
    }

    function renderDots(count) {
      dotsList.innerHTML = '';
      for (let i = 0; i < count; i++) {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'reviews-carousel__dot';
        btn.setAttribute('aria-label', `Go to review ${i + 1}`);
        btn.dataset.index = String(i);
        li.appendChild(btn);
        dotsList.appendChild(li);
      }
    }

    let activeIndex = 0;
    let autoplayTimer = null;
    let userPaused = false;

    function syncPlayPauseButton() {
      if (!playPauseBtn) return;
      const icon = playPauseBtn.querySelector('.reviews-carousel__play-pause-icon');
      if (userPaused) {
        playPauseBtn.setAttribute('aria-label', 'Resume auto-advance');
        playPauseBtn.setAttribute('aria-pressed', 'true');
        if (icon) icon.textContent = '\u25B6';
      } else {
        playPauseBtn.setAttribute('aria-label', 'Pause auto-advance');
        playPauseBtn.setAttribute('aria-pressed', 'false');
        if (icon) icon.textContent = '\u23F8';
      }
    }

    function updateActiveUi(idx) {
      activeIndex = idx;
      const dots = dotsList.querySelectorAll('.reviews-carousel__dot');
      dots.forEach((d, i) => {
        d.classList.toggle('is-active', i === idx);
        if (i === idx) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
      // Announce slide change for screen readers
      if (liveRegion) {
        const slide = track.children[idx];
        const cite = slide && slide.querySelector('.reviews-carousel__cite');
        liveRegion.textContent = `Review ${idx + 1} of ${track.children.length}${cite ? ': ' + cite.textContent.trim() : ''}`;
      }
    }

    function scrollToIndex(idx, smooth = true) {
      const slide = track.children[idx];
      if (!slide) return;
      // Use scrollLeft directly to avoid the page scrolling. scroll-snap-align:
      // start ensures the snap aligns slide.left to track.left at rest.
      track.scrollTo({
        left: slide.offsetLeft,
        behavior: smooth && !reduceMotion ? 'smooth' : 'auto',
      });
    }

    function go(delta) {
      const total = track.children.length;
      if (!total) return;
      const next = (activeIndex + delta + total) % total;
      scrollToIndex(next);
    }

    function startAutoplay() {
      // Note: `prefers-reduced-motion` no longer gates the timer itself —
      // the explicit play/pause button below lets users opt out. Reduced
      // motion still applies to the per-slide scroll (instant, not smooth).
      if (userPaused) return;
      stopAutoplay();
      autoplayTimer = window.setInterval(() => go(1), AUTOPLAY_MS);
    }

    function stopAutoplay() {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    }

    function setupObserver() {
      // Track which slide is most visible in the viewport.
      const io = new IntersectionObserver((entries) => {
        // Pick entry with the highest intersectionRatio; update active.
        let best = null;
        for (const entry of entries) {
          if (!best || entry.intersectionRatio > best.intersectionRatio) best = entry;
        }
        if (best && best.isIntersecting && best.intersectionRatio >= 0.6) {
          const idx = parseInt(best.target.dataset.index || '0', 10);
          if (idx !== activeIndex) updateActiveUi(idx);
        }
      }, { root: track, threshold: [0.6, 0.8, 1.0] });
      Array.from(track.children).forEach((c) => io.observe(c));
    }

    function bindEvents() {
      prevBtn.addEventListener('click', () => { stopAutoplay(); go(-1); startAutoplay(); });
      nextBtn.addEventListener('click', () => { stopAutoplay(); go(1); startAutoplay(); });
      dotsList.addEventListener('click', (e) => {
        const btn = e.target.closest('.reviews-carousel__dot');
        if (!btn) return;
        const idx = parseInt(btn.dataset.index || '0', 10);
        stopAutoplay();
        scrollToIndex(idx);
        startAutoplay();
      });
      // Global arrow-key navigation. Active when the carousel is in the
      // viewport and the user is not typing. Skips when modifier keys are
      // held so we don't shadow browser shortcuts (e.g. Cmd+Left for back).
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        const t = e.target;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
        const rect = root.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        if (rect.bottom < 0 || rect.top > vh) return;
        e.preventDefault();
        stopAutoplay();
        go(e.key === 'ArrowLeft' ? -1 : 1);
        startAutoplay();
      });
      // Pause autoplay on focus only — viewport hover-pause was too greedy
      // (any mouse cursor parked over the carousel would freeze it). The
      // explicit play/pause button below covers the WCAG 2.2.2 requirement.
      viewport.addEventListener('focusin', stopAutoplay);
      viewport.addEventListener('focusout', startAutoplay);
      // Pause when document is hidden (tab switched)
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) stopAutoplay();
        else startAutoplay();
      });
      // Explicit play/pause toggle. Sticky: a paused carousel stays paused
      // through hover/focus/visibility events until the user clicks again.
      if (playPauseBtn) {
        playPauseBtn.addEventListener('click', () => {
          userPaused = !userPaused;
          syncPlayPauseButton();
          if (userPaused) stopAutoplay();
          else startAutoplay();
        });
      }
    }

    // index.html ships the first review as static HTML (it is the mobile LCP
    // element). It carries data-id = the review's id in reviews.json.
    const staticSlide = track.querySelector('.reviews-carousel__slide[data-id]');
    const staticId = staticSlide ? staticSlide.dataset.id : null;

    fetch(src, { credentials: 'omit' })
      .then((res) => {
        if (!res.ok) throw new Error('reviews fetch failed: ' + res.status);
        return res.json();
      })
      .then((data) => {
        const all = Array.isArray(data && data.reviews) ? data.reviews : [];
        // Strict filter: only entries with enabled === true survive. Disabled
        // entries are excluded entirely — they don't consume slide indices or
        // dot-strip slots. (Entries missing the field are also excluded so a
        // forgotten flag never silently re-enables a removed review.)
        const reviews = all.filter((r) => r && r.enabled === true);
        // Keep the static slide only while its review is still enabled.
        const keepStatic = !!staticSlide && reviews.some((r) => r.id === staticId);
        if (staticSlide && !keepStatic) staticSlide.remove();
        const rest = keepStatic ? reviews.filter((r) => r.id !== staticId) : reviews;
        if (!rest.length && !keepStatic) {
          // Hide the section entirely if no reviews to show.
          root.hidden = true;
          return;
        }
        // The static slide stays first; the other reviews follow in random
        // order so visitors see different ones over time.
        renderReviews(shuffle(rest));
        renumberSlides();
        renderDots(track.children.length);
        prevBtn.hidden = false;
        nextBtn.hidden = false;
        if (playPauseBtn) playPauseBtn.hidden = false;
        // Start on slide 1 without scrolling: the track is already at 0.
        updateActiveUi(0);
        syncPlayPauseButton();
        setupObserver();
        bindEvents();
        startAutoplay();
      })
      .catch((err) => {
        // Non-critical. The static slide (if any) stays visible and the
        // controls stay hidden; hide the section only when it is empty.
        // eslint-disable-next-line no-console
        console.warn('[reviews-carousel]', err);
        if (!track.children.length) root.hidden = true;
        // Release the min-heights reserved for the loaded carousel (CSS).
        root.classList.add('reviews-carousel--static');
      });
  }

  // Defer until DOM is parseable. site.js is loaded with `defer`, so DOM is ready.
  initReviewsCarousel();

  // ---------------------------------------------------------------------------
  // WellnessLiving widget a11y patches.
  // ---------------------------------------------------------------------------
  // The third-party widgets (a) inject <iframe>s without a title and
  // (b) rewrite <meta name="viewport"> to add maximum-scale=1, which blocks
  // pinch zoom (WCAG 1.4.4). We cannot change the widgets, so we patch the DOM
  // after they act. The observers stop after 30 s; the widgets are done by then.
  const WL_FRAME_TITLES = [
    ['wl-lead-capture-widget', 'Newsletter sign-up form'],
    ['wl-store-widget', 'Online store: classes and memberships'],
    ['.wl-widget', 'Class schedule and booking'],
  ];

  function patchWellnessLivingWidget() {
    if (!('MutationObserver' in window)) return;
    const observers = [];

    function titleFrames() {
      WL_FRAME_TITLES.forEach(([container, title]) => {
        document.querySelectorAll(`${container} iframe:not([title])`).forEach((f) => {
          f.setAttribute('title', title);
        });
      });
    }

    const widgets = document.querySelectorAll(WL_FRAME_TITLES.map(([c]) => c).join(','));
    if (widgets.length) {
      titleFrames();
      const frameObserver = new MutationObserver(titleFrames);
      widgets.forEach((w) => frameObserver.observe(w, { childList: true, subtree: true }));
      observers.push(frameObserver);
    }

    const viewportMeta = document.querySelector('meta[name="viewport"]');
    if (viewportMeta) {
      const fixViewport = () => {
        const content = viewportMeta.getAttribute('content') || '';
        if (!/maximum-scale/i.test(content)) return;
        let fixed = 'width=device-width, initial-scale=1';
        if (/interactive-widget\s*=\s*resizes-content/i.test(content)) {
          fixed += ', interactive-widget=resizes-content';
        }
        viewportMeta.setAttribute('content', fixed);
      };
      fixViewport();
      const metaObserver = new MutationObserver(fixViewport);
      metaObserver.observe(viewportMeta, { attributes: true, attributeFilter: ['content'] });
      observers.push(metaObserver);
    }

    if (observers.length) {
      window.setTimeout(() => observers.forEach((o) => o.disconnect()), 30000);
    }
  }
  patchWellnessLivingWidget();

  // ---------------------------------------------------------------------------
  // Scroll-reveal utility (Phase 3 hook). Inactive until elements opt in via
  // [data-reveal]. Adds .is-revealed when ≥10% of the element enters the viewport.
  // ---------------------------------------------------------------------------
  if ('IntersectionObserver' in window) {
    const reveals = document.querySelectorAll('[data-reveal]');
    if (reveals.length) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            io.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 });
      reveals.forEach((el) => io.observe(el));
    }
  }
})();
